import type {
  ChatMessageResponse,
  ChatSessionWithMessagesResponse,
} from '@karehero/models';

// A refetch can come back before the writes queued behind it land, so taken
// as it is it drops messages already shown and plays them in again once they
// are saved. These keep what is shown, and take from the server only what
// it learns on its own: new messages, documents, failures and rejections.

const FAILED = 'error';

/** One message as shown, with what the server has learned about it since. */
const mergeMessage = (
  shown: ChatMessageResponse,
  fetched: ChatMessageResponse,
): ChatMessageResponse => ({
  ...shown,
  content: { ...fetched.content, ...shown.content } as typeof shown.content,
  documentId: shown.documentId ?? fetched.documentId,
  // The server lags what is shown, except when it says a message failed.
  status: fetched.status === FAILED ? FAILED : shown.status,
  careProfileErrors: fetched.careProfileErrors ?? shown.careProfileErrors,
});

/** The messages as shown, in their order, then any only the server has. */
export const mergeMessages = (
  shown: readonly ChatMessageResponse[],
  fetched: readonly ChatMessageResponse[],
): ChatMessageResponse[] => {
  const fetchedByID = new Map(fetched.map((m) => [m.id, m]));
  const shownIDs = new Set(shown.map((m) => m.id));
  return [
    ...shown.map((m) => {
      const match = fetchedByID.get(m.id);
      return match ? mergeMessage(m, match) : m;
    }),
    ...fetched.filter((m) => !shownIDs.has(m.id)),
  ];
};

/**
 * The sessions as the server lists them, each keeping the messages already
 * shown for it; a session the server no longer lists is dropped.
 */
export const mergeChatSessions = (
  shown: readonly ChatSessionWithMessagesResponse[],
  fetched: readonly ChatSessionWithMessagesResponse[],
): ChatSessionWithMessagesResponse[] =>
  fetched.map((incoming) => {
    const current = shown.find((s) => s.session.id === incoming.session.id);
    if (!current) return incoming;
    return {
      session: incoming.session,
      messages: mergeMessages(current.messages, incoming.messages),
    };
  });
